import { defineSlotRecipe } from "@chakra-ui/react";

/**
 * Chakra v3 Checkbox Slot Recipe
 *
 * Slots: root, control, indicator, label
 *
 * Usage in component:
 *   <Checkbox.Root size="md">
 *     <Checkbox.HiddenInput />
 *     <Checkbox.Control>
 *       <Checkbox.Indicator />
 *     </Checkbox.Control>
 *     <Checkbox.Label>Label text</Checkbox.Label>
 *   </Checkbox.Root>
 */
const checkboxSlotRecipe = defineSlotRecipe({
  className: "checkbox",
  slots: ["root", "control", "indicator", "label"],
  base: {
    root: {
      display: "inline-flex",
      alignItems: "center",
      gap: "sm",
      cursor: "pointer",
      // Sets the color palette for Chakra's default _checked/_indeterminate styles
      // This makes colorPalette.solid resolve to primary.solid
      colorPalette: "primary",
      _disabled: {
        cursor: "not-allowed",
        opacity: 0.6,
      },
    },
    control: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      borderWidth: "1px",
      borderStyle: "solid",
      borderRadius: "sm",
      p: "2xs",
      borderColor: "colorPalette.solid",
      color: "transparent",
      bg: "bg",
      transition: "all 0.2s",

      _checked: {
        bg: "colorPalette.solid",
        borderColor: "colorPalette.solid",
        color: "colorPalette.contrast",

        _hover: {
          bg: "colorPalette.emphasized",
          borderColor: "colorPalette.emphasized",
        },

        _disabled: {
          borderColor: "colorPalette.muted",
          bg: "colorPalette.muted",
          color: "white",
        },
      },

      _indeterminate: {
        bg: "colorPalette.solid",
        borderColor: "colorPalette.solid",
        color: "fg",
      },

      _hover: {
        cursor: "pointer",
        borderColor: "colorPalette.solid",
      },

      _disabled: {
        bg: "bg",
        borderColor: "gray.300",

        _hover: {
          borderColor: "gray.300",
        },

        _checked: {
          bg: "colorPalette.solid",
          borderColor: "colorPalette.solid",
          color: "white",
        },
      },

      _focusVisible: {
        boxShadow: "outline",
      },

      _invalid: {
        borderColor: "error.solid",
      },
    },
    indicator: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "inherit",
    },
    label: {
      userSelect: "none",
      color: "fg",
      _disabled: {
        opacity: 0.6,
      },
    },
  },
  variants: {
    size: {
      sm: {
        control: {
          width: "16px",
          height: "16px",
        },
        label: {
          fontSize: "sm",
        },
      },
      md: {
        control: {
          width: "20px",
          height: "20px",
        },
        label: {
          fontSize: "md",
        },
      },
      lg: {
        control: {
          width: "24px",
          height: "24px",
        },
        label: {
          fontSize: "lg",
        },
      },
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export default checkboxSlotRecipe;
